import './intercom.script';
import { User } from '../types';
import { shouldSkipIntercom } from './intercomLoading';
import { FF_BETA_CHANNEL_CUSTOMER } from '@upheal/utils/feature-flags';
import {
    INTERCOM_SIDEBAR_GAP,
    SIDEBAR_WIDTH_EXPANDED,
} from '@upheal/design-system/components/Sidebar/constants';
import IntercomSettings = Intercom_.IntercomSettings;

type IntercomPlacementSettings = Pick<
    IntercomSettings,
    'alignment' | 'horizontal_padding' | 'hide_default_launcher'
>;

const DEFAULT_INTERCOM_PLACEMENT: IntercomPlacementSettings = {
    alignment: 'left',
    horizontal_padding: SIDEBAR_WIDTH_EXPANDED + INTERCOM_SIDEBAR_GAP,
    hide_default_launcher: true,
};

const SIDEBAR_TRANSITION = '0.15s ease-in-out';

const INTERCOM_TRANSITION_STYLE_ID = 'intercom-transition-style';

const injectIntercomTransitionStyle = () => {
    if (document.getElementById(INTERCOM_TRANSITION_STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = INTERCOM_TRANSITION_STYLE_ID;
    style.textContent = `
        .intercom-messenger-frame,
        .intercom-lightweight-app,
        .intercom-launcher-frame,
        .intercom-notifications-frame {
            transition: left ${SIDEBAR_TRANSITION}, right ${SIDEBAR_TRANSITION} !important;
        }
    `;
    document.head.appendChild(style);
};

const removeIntercomTransitionStyle = () => {
    document.getElementById(INTERCOM_TRANSITION_STYLE_ID)?.remove();
};

type UnreadCountListener = (count: number) => void;

const unreadCountListeners = new Set<UnreadCountListener>();

const intercom = {
    isInitiated: false,
    isEnabled: true,
    unreadCount: 0,
    preparedConfig: undefined as typeof window.intercomSettings,

    prepareInit(user: User) {
        if (shouldSkipIntercom()) return;

        this.preparedConfig = {
            api_base: 'https://api-iam.intercom.io',
            app_id: `${import.meta.env.VITE_REACT_APP_INTERCOM_APP_ID}`,
            name: `${user.firstName} ${user.lastName}`,
            email: user.email ?? undefined,
            user_id: user.id,
            ...(user.hmac && { user_hash: user.hmac }),
            // 'Beta Program' is defined in https://app.intercom.com/a/apps/wrl75la4/settings/data/people?tab=attributes
            ...(FF_BETA_CHANNEL_CUSTOMER.on && { 'Beta Program': true }),
            hide_default_launcher: true,
        };
    },

    init(user?: User) {
        if (shouldSkipIntercom()) return;

        if (user) {
            this.prepareInit(user);
        }

        if (!this.preparedConfig) {
            throw new Error(
                'intercom.init() called without user. Either call prepareInit() or pass user as argument.',
            );
        }

        injectIntercomTransitionStyle();
        window.Intercom('boot', {
            ...this.preparedConfig,
            ...DEFAULT_INTERCOM_PLACEMENT,
        });
        window.Intercom('onUnreadCountChange', (count: number) => {
            this.unreadCount = count;
            unreadCountListeners.forEach((listener) => listener(count));
        });
        this.isInitiated = true;
    },

    show() {
        if (shouldSkipIntercom()) return;

        if (!this.isEnabled) return;
        if (!this.preparedConfig) return;
        if (!this.isInitiated) {
            this.init();
        }
        window.Intercom('show');
    },

    showNewMessage(message?: string) {
        if (shouldSkipIntercom()) return;

        if (!this.isEnabled) return;
        if (!this.preparedConfig) return;
        if (!this.isInitiated) {
            this.init();
        }
        window.Intercom('showNewMessage', message ?? '');
    },

    updateSidebarWidth(sidebarWidth: number) {
        if (shouldSkipIntercom()) return;

        if (!this.isEnabled) return;
        if (!this.preparedConfig) return;
        if (!this.isInitiated) {
            this.init();
        }
        window.Intercom('update', {
            alignment: 'left',
            horizontal_padding: sidebarWidth,
            hide_default_launcher: true,
        });
    },

    onUnreadCountChange(listener: UnreadCountListener) {
        unreadCountListeners.add(listener);
        return () => {
            unreadCountListeners.delete(listener);
        };
    },

    hide() {
        if (shouldSkipIntercom()) return;
        if (!this.isInitiated) return;
        window.Intercom('hide');
    },

    shutdown() {
        if (shouldSkipIntercom()) return;

        if (this.isInitiated) {
            window.Intercom('shutdown');
            removeIntercomTransitionStyle();
            this.isInitiated = false;
        }
    },
};

export default intercom;
